'use client'

import React, { useEffect, useMemo, useRef, useState } from 'react'
import Image from 'next/image'

type Slide = { src: string; alt?: string }

type Props = {
  images: Slide[]
  autoPlay?: boolean
  interval?: number
  loop?: boolean
  showDots?: boolean
  showArrows?: boolean
  aspectRatio?: string // CSS aspect-ratio if no width/height
  width?: number | string // e.g. 800 or "100%"
  height?: number | string // e.g. 400 or "50vh"
  roundedClass?: string
  rtl?: boolean
  pauseOnHover?: boolean
  className?: string
}

export default function ImageSlider({
  images,
  autoPlay = true,
  interval = 3000,
  loop = true,
  showDots = true,
  // showArrows = true,
  aspectRatio = '16 / 9',
  width,
  height,
  roundedClass = 'rounded-sm',
  // rtl = false,
  pauseOnHover = true,
  className = '',
}: Props) {
  const safeImages = useMemo(() => images ?? [], [images])
  const [index, setIndex] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  // Drag offset as a percentage of the wrapper width, computed in the
  // pointer handlers so render never reads wrapRef.current.
  const [dragPct, setDragPct] = useState(0)
  const wrapRef = useRef<HTMLDivElement | null>(null)
  const hoverRef = useRef(false)
  const visRef = useRef(document.visibilityState === 'visible')
  const timerRef = useRef<number | null>(null)

  const count = safeImages.length

  const goTo = React.useCallback(
    (i: number) => {
      if (!loop) {
        setIndex(() => Math.max(0, Math.min(count - 1, i)))
      } else {
        setIndex(() => (i + count) % count)
      }
    },
    [count, loop]
  )

  const next = React.useCallback(() => {
    setIndex((i) =>
      loop ? (i + 1) % count : Math.max(0, Math.min(count - 1, i + 1))
    )
  }, [loop, count])

  const prev = React.useCallback(() => {
    setIndex((i) =>
      loop ? (i - 1 + count) % count : Math.max(0, Math.min(count - 1, i - 1))
    )
  }, [loop, count])

  // Autoplay
  useEffect(() => {
    if (!autoPlay || count <= 1) return
    const start = () => {
      stop()
      timerRef.current = window.setInterval(() => {
        if (pauseOnHover && hoverRef.current) return
        if (!visRef.current) return
        next()
      }, Math.max(1200, interval))
    }
    const stop = () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
    }
    start()
    const onVis = () => {
      visRef.current = document.visibilityState === 'visible'
    }
    document.addEventListener('visibilitychange', onVis)
    return () => {
      stop()
      document.removeEventListener('visibilitychange', onVis)
    }
  }, [autoPlay, interval, count, pauseOnHover, next])

  // Hover pause
  const onMouseEnter = () => {
    if (pauseOnHover) hoverRef.current = true
  }
  const onMouseLeave = () => {
    if (pauseOnHover) hoverRef.current = false
  }

  // Drag & Swipe
  useEffect(() => {
    const el = wrapRef.current
    if (!el) return
    let startX = 0
    let lastX = 0
    let active = false

    const threshold = () => el.clientWidth * 0.15
    const start = (x: number) => {
      active = true
      setIsDragging(true)
      startX = x
      lastX = x
      setDragPct(0)
    }
    const move = (x: number) => {
      if (!active) return
      lastX = x
      setDragPct(el.clientWidth ? ((x - startX) / el.clientWidth) * 100 : 0)
    }
    const end = () => {
      if (!active) return
      const delta = lastX - startX
      active = false
      setIsDragging(false)
      setDragPct(0)
      if (Math.abs(delta) > threshold()) {
        if (delta < 0) {
          next()
        } else {
          prev()
        }
      }
    }

    const onDown = (e: PointerEvent) => {
      el.setPointerCapture(e.pointerId)
      start(e.clientX)
    }
    const onMove = (e: PointerEvent) => move(e.clientX)
    const onUp = () => end()
    const onTouchStart = (e: TouchEvent) => start(e.touches[0].clientX)
    const onTouchMove = (e: TouchEvent) => move(e.touches[0].clientX)
    const onTouchEnd = () => end()

    el.addEventListener('pointerdown', onDown)
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    el.addEventListener('touchstart', onTouchStart, { passive: true })
    el.addEventListener('touchmove', onTouchMove, { passive: true })
    el.addEventListener('touchend', onTouchEnd)

    return () => {
      el.removeEventListener('pointerdown', onDown)
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      el.removeEventListener('touchstart', onTouchStart)
      el.removeEventListener('touchmove', onTouchMove)
      el.removeEventListener('touchend', onTouchEnd)
    }
  }, [next, prev])

  if (count === 0) {
    return <div>No images</div>
  }

  const translatePct = -index * 100
  const totalTranslate = translatePct + dragPct

  const wrapperStyle: React.CSSProperties = {
    width: width ?? '100%',
    height: height ?? 'auto',
    ...(width && height ? {} : { aspectRatio }),
  }

  return (
    <div
      className={`relative overflow-hidden ${roundedClass} ${className}  pb-8 sm:pb-0`}
      style={wrapperStyle}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div ref={wrapRef} className="h-full w-full">
        <div
          className="sm:flex hidden h-full"
          style={{
            width: `${count * 100}%`,
            transform: `translateX(${totalTranslate}%)`,
            transition: isDragging ? 'none' : 'transform 350ms ease',
          }}
        >
          {safeImages.map((img, i) => (
            <div key={i} className="relative h-full w-full shrink-0 ">
              <Image
                src={img.src}
                alt={img.alt ?? `Slide ${i + 1}`}
                fill
                sizes="100vw"
                className={`object-cover max-w-[236px] ${roundedClass}`}
                draggable={false}
                unoptimized
                priority={i === 0}
              />
            </div>
          ))}
        </div>
        <div
          className="sm:hidden flex h-full"
          style={{
            width: `${count * 100}%`,
            transform: `translateX(${totalTranslate}%)`,
            transition: isDragging ? 'none' : 'transform 350ms ease',
          }}
        >
          {safeImages.map((img, i) => {
            const imgUrl = img.src.split('.avif')[0] + '-mobile' + '.avif'

            return (
              <div key={i} className="relative h-full w-full shrink-0 ">
                <Image
                  src={imgUrl}
                  alt={img.alt ?? `Slide ${i + 1}`}
                  fill
                  sizes="100vw"
                  className={`object-cover max-w-[309px] ${roundedClass}`}
                  draggable={false}
                  unoptimized
                  priority={i === 0}
                />
              </div>
            )
          })}
        </div>
      </div>
      {showDots && count > 1 && (
        <div className="flex justify-center gap-2 absolute bottom-1 sm:bottom-2 left-0 right-0 ">
          {safeImages.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              className={`h-2 w-2 rounded-full ${
                i === index
                  ? 'bg-green-main w-5'
                  : 'bg-[#D9D9D9] sm:bg-white/50'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  )
}
